import React from "react";
import { Link, useLocation } from "react-router-dom";
import { NavigationItem } from "../../../types";
import { cn } from "../../../utils/cn";
import { ChevronDown } from "lucide-react";

interface SidebarItemProps {
  item: NavigationItem;
  collapsedSections: Set<string>;
  onToggleSection: (sectionId: string) => void;
  level: number;
  isSidebarCollapsed: boolean;
}

export const SidebarItem: React.FC<SidebarItemProps> = ({
  item,
  collapsedSections,
  onToggleSection,
  level,
  isSidebarCollapsed,
}) => {
  const location = useLocation();
  const isActive = location.pathname === item.path;
  const hasChildren = item.children && item.children.length > 0;
  const isCollapsed = collapsedSections.has(item.id);

  const handleClick = (e: React.MouseEvent) => {
    if (hasChildren) {
      e.preventDefault();
      onToggleSection(item.id);
    }
  };

  // Tooltip content for collapsed state
  const tooltipContent = isSidebarCollapsed ? item.label : "";

  return (
    <div className="relative group">
      <Link
        to={item.path}
        onClick={handleClick}
        className={cn(
          "flex items-center gap-3 px-3 py-2 rounded-xl text-base font-bold transition-colors",
          "hover:bg-sidebar-background hover:text-teal",
          isActive ? "bg-sidebar-background text-teal" : "text-white",
          level > 0 && !isSidebarCollapsed && "ml-4",
          isSidebarCollapsed && "justify-center px-2",
        )}
        title={tooltipContent}
      >
        {item.icon && (
          <span
            className={cn(
              "flex-shrink-0 transition-transform duration-200",
              isSidebarCollapsed ? "text-lg" : "text-base",
            )}
          >
            <item.icon size={24} />
          </span>
        )}

        {/* Label - hidden when sidebar is collapsed */}
        {!isSidebarCollapsed && (
          <>
            <span className="flex-1 truncate">{item.label}</span>
            {hasChildren && (
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform flex-shrink-0",
                  isCollapsed && "-rotate-90",
                )}
              />
            )}
          </>
        )}
      </Link>

      {/* Tooltip for collapsed state */}
      {isSidebarCollapsed && (
        <div
          className={cn(
            "absolute left-full top-1/2 transform -translate-y-1/2 ml-2",
            "bg-foreground text-background px-2 py-1 rounded-lg text-sm",
            "opacity-0 group-hover:opacity-100 transition-opacity duration-200",
            "pointer-events-none z-50 whitespace-nowrap",
            "shadow-lg border border-border",
          )}
        >
          <p className="text-teal text-xl font-medium">
          {item.label}
          </p>
          {/* Tooltip arrow */}
          <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-foreground" />
        </div>
      )}

      {/* Children - only show when sidebar is expanded */}
      {hasChildren && !isCollapsed && !isSidebarCollapsed && (
        <div className="mt-1 space-y-1">
          {item.children!.map((child) => (
            <SidebarItem
              key={child.id}
              item={child}
              collapsedSections={collapsedSections}
              onToggleSection={onToggleSection}
              level={level + 1}
              isSidebarCollapsed={isSidebarCollapsed}
            />
          ))}
        </div>
      )}
    </div>
  );
};
